/**
 * Permissions catalog API — system-wide (Milestone 1.5b).
 *
 * Reads open to any auth user; writes require system_role=founder.
 *
 * Mirrors apps/chaos-gateway/src/permissions.ts.
 */
import {
  createPermissionSchema,
  permissionCatalogSchema,
  updatePermissionSchema,
  type CreatePermissionInput,
  type PermissionCatalog,
  type UpdatePermissionInput,
} from '@chaos/types/registries';
import { apiJson } from './client';
import { normalizeOrgPublications } from './org-publication-shape';

/*
 * Keep the schemas at this wire boundary. TypeScript cannot protect the UI
 * from an older gateway omitting a required classification, nor from an
 * untyped caller trying to put the write-once field back into PATCH.
 */
interface PermissionsListResponse {
  permissions: PermissionCatalog[];
}
interface PermissionResponse {
  permission: PermissionCatalog;
}

function parsePermission(value: unknown): PermissionCatalog {
  return permissionCatalogSchema.parse(value);
}

// ── Cross-org publication matrix (0079) ────────────────────────────────
// MIRRORS skillsApi (0078) EXACTLY, except the item key is the TEXT `permission`
// slug (not a uuid). A permission is PUBLISHED (made available) into an org via a
// permission_org_publications row. `allOrgs` = the all-organizations sentinel (a
// NULL org_id row server-side): when true the permission is available in EVERY
// org of the account and the per-org cells render as inherited. `orgIds` is the
// explicit per-org set. FOUNDER-RESOLVED semantics: pure checkbox — All-orgs →
// everywhere; specific org rows → those; no rows → available nowhere.
// Publication is the availability gate; the per-role grant (role_permissions) is
// UNTOUCHED.
/**
 * WHERE an item is published, and which of those marks the "All organizations"
 * select-all button put there (migration 0168).
 *
 *   orgIds       — every organization the item is published into. THE list.
 *   viaAllOrgIds — the subset select-all placed (⊆ orgIds). The origin is
 *                  remembered because two rules depend on it: a new solo
 *                  Workspace inherits these marks, and the first other person
 *                  added to a Workspace switches them off. Marks placed by hand
 *                  are never touched by either.
 *
 * There is no `allOrgs` boolean any more. "Available everywhere" used to be a
 * separate STATUS row (org_id IS NULL) which made it impossible to exclude a
 * single organization — and which, because no read gate carried an account
 * filter, actually meant "every organization that will ever exist, on any
 * account". It is now simply the state where every organization is ticked,
 * which the UI reads off `orgIds` and therefore cannot disagree about.
 */
export interface PermissionPublications {
  orgIds: string[];
  viaAllOrgIds: string[];
}

// One column of the cross-org matrix — an org of the caller's account.
export interface PermissionMatrixOrg {
  id: string;
  displayName: string;
  logoUrl: string | null;
}

// One row of the matrix (GET /api/permissions/all): the catalog permission plus
// its publication set. Permissions have NO owner org (global catalog).
export interface PermissionMatrixRow extends PermissionCatalog {
  publications: PermissionPublications;
}

export interface PermissionMatrix {
  permissions: PermissionMatrixRow[];
  orgs: PermissionMatrixOrg[];
}

interface PermissionPublicationsResponse {
  publications: PermissionPublications;
}

interface ListPermissionsParams {
  category?: string;
  [k: string]: string | number | boolean | undefined;
}

function qs(params: Record<string, string | number | boolean | undefined>): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    usp.set(k, String(v));
  }
  const s = usp.toString();
  return s ? `?${s}` : '';
}

export const permissionsApi = {
  list(params: ListPermissionsParams = {}): Promise<PermissionCatalog[]> {
    return apiJson<PermissionsListResponse>(`/permissions${qs(params)}`).then(
      (r) => r.permissions.map(parsePermission),
    );
  },
  // 0079: account-wide matrix — every permission in the global catalog, each with
  // its cross-org publication set, plus the account's orgs as the matrix columns.
  // Account-owner only (403 otherwise). Mirrors skillsApi.listAll().
  //
  // THE PUBLICATION SETS ARE NORMALISED AT THE DOOR (see
  // ./org-publication-shape) — the same guarantee <CrossOrgMatrix> already gets
  // from the connectors, templates and roles readers it shares. Take it out and
  // a payload whose `orgIds` is not an array takes the section down through
  // `cellState`, which React evaluates while building the element tree.
  listAll(): Promise<PermissionMatrix> {
    return apiJson<PermissionMatrix>(`/permissions/all`).then((m) => ({
      ...m,
      permissions: m.permissions.map((p) => {
        const catalog = parsePermission(p);
        return {
          ...catalog,
          publications: normalizeOrgPublications(p.publications),
        };
      }),
    }));
  },
  get(permission: string): Promise<PermissionCatalog> {
    return apiJson<PermissionResponse>(`/permissions/${encodeURIComponent(permission)}`).then(
      (r) => parsePermission(r.permission),
    );
  },
  create(input: CreatePermissionInput): Promise<PermissionCatalog> {
    const body = createPermissionSchema.parse(input);
    return apiJson<PermissionResponse>('/permissions', { method: 'POST', json: body }).then(
      (r) => parsePermission(r.permission),
    );
  },
  update(permission: string, input: UpdatePermissionInput): Promise<PermissionCatalog> {
    const body = updatePermissionSchema.parse(input);
    return apiJson<PermissionResponse>(`/permissions/${encodeURIComponent(permission)}`, {
      method: 'PATCH',
      json: body,
    }).then((r) => parsePermission(r.permission));
  },
  delete(permission: string): Promise<{ ok: boolean; permission: PermissionCatalog }> {
    return apiJson<{ ok: boolean; permission: PermissionCatalog }>(
      `/permissions/${encodeURIComponent(permission)}`,
      { method: 'DELETE' },
    ).then((r) => ({ ...r, permission: parsePermission(r.permission) }));
  },
  // ── 0079: cross-org publication (item key = TEXT permission slug) ──────
  // Reconcile the FULL publication set in one call. Mirrors skillsApi.setPublications().
  setPublications(
    permission: string,
    pub: PermissionPublications,
  ): Promise<PermissionPublications> {
    return apiJson<PermissionPublicationsResponse>(
      `/permissions/${encodeURIComponent(permission)}/publications`,
      {
        method: 'PUT',
        json: { orgIds: pub.orgIds },
      },
    ).then((r) => r.publications);
  },
  // Toggle ONE matrix cell ON. orgId=null = the all-organizations sentinel.
  // Tick ONE organization. 0168: `orgId: null` used to mean the sentinel and is
  // now a 400 — selectAll() is the route for that.
  addPublication(permission: string, orgId: string): Promise<PermissionPublications> {
    return apiJson<PermissionPublicationsResponse>(`/permissions/${permission}/publications`, {
      method: 'POST',
      json: { orgId },
    }).then((r) => r.publications);
  },

  // THE SELECT-ALL. Puts a separate mark on every organization of the account;
  // any one of them can then be unticked on its own.
  selectAllOrgs(permission: string): Promise<PermissionPublications> {
    return apiJson<PermissionPublicationsResponse>(`/permissions/${permission}/publications/all`, {
      method: 'POST',
    }).then((r) => r.publications);
  },

  // The same box, unticked. IT REMOVES ONLY THE MARKS IT MADE — hand-placed
  // marks survive, which is why the header box can end up half-selected.
  clearAllOrgs(permission: string): Promise<PermissionPublications> {
    return apiJson<PermissionPublicationsResponse>(`/permissions/${permission}/publications/all`, {
      method: 'DELETE',
    }).then((r) => r.publications);
  },
  // Toggle ONE matrix cell OFF. orgId=null = remove the all-orgs sentinel.
  // Untick ONE organization. Works the same whether the mark was hand-placed or
  // put there by select-all — "after that, any one of them can be unticked" is
  // the whole point of 0168.
  removePublication(permission: string, orgId: string): Promise<PermissionPublications> {
    return apiJson<PermissionPublicationsResponse>(
      `/permissions/${permission}/publications?orgId=${encodeURIComponent(orgId)}`,
      { method: 'DELETE' },
    ).then((r) => r.publications);
  },
  // Convenience: flip one cell to the desired state. Mirrors togglePublication().
  // Flip one matrix cell. `orgId === null` is the HEADER box (select-all), which
  // since 0168 is a real bulk write rather than a status flag.
  togglePublication(
    permission: string,
    orgId: string | null,
    on: boolean,
  ): Promise<PermissionPublications> {
    if (orgId === null) {
      return on ? this.selectAllOrgs(permission) : this.clearAllOrgs(permission);
    }
    return on ? this.addPublication(permission, orgId) : this.removePublication(permission, orgId);
  },
};

export const permissionsKeys = {
  all: ['permissions'] as const,
  list: (params: ListPermissionsParams = {}) => [...permissionsKeys.all, 'list', params] as const,
  detail: (permission: string) => [...permissionsKeys.all, 'detail', permission] as const,
  // 0079: account-wide publication matrix (GET /permissions/all).
  matrix: () => [...permissionsKeys.all, 'matrix'] as const,
};
